export interface KeyValuePair {
    key: string;
    value: string;
    description?: string;
    appName?: string;
}

export interface GlobalKeyValueStore {
    [appName: string]: KeyValuePair[];
}

export class GlobalKeyValueService {
    private static readonly STORAGE_KEY = 'globalKeyValuePairs';
    
    static getKeyValuePairs(appName?: string): KeyValuePair[] {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            if (!stored) return [];
            
            const allPairs: GlobalKeyValueStore = JSON.parse(stored);
            
            if (appName) {
                return allPairs[appName] || [];
            }
            
            return Object.values(allPairs).flat();
        } catch (error) {
            console.error('Error retrieving key-value pairs:', error);
            return [];
        }
    }

    static getKeyValuePairsWithUrlFallback(appName?: string): KeyValuePair[] {
        const pairs = this.getKeyValuePairs(appName);
        const urlParams = new URLSearchParams(window.location.search);
        
        const urlParamMap = new Map<string, string>();
        urlParams.forEach((value, key) => {
            urlParamMap.set(key.toLowerCase(), value);
        });
        
        const processedPairs = pairs.map(pair => {
            const urlValue = urlParamMap.get(pair.key.toLowerCase());
            if (urlValue !== undefined) {
                return { ...pair, value: urlValue };
            }
            if (!pair.value || pair.value.trim() === '') {
                const urlValue = urlParamMap.get(pair.key.toLowerCase());
                if (urlValue !== undefined) {
                    return { ...pair, value: urlValue };
                }
            }
            return pair;
        });
        return processedPairs;
    }
    
    static setKeyValuePairs(appName: string, pairs: KeyValuePair[]): void {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            const allPairs: GlobalKeyValueStore = stored ? JSON.parse(stored) : {};
            
            allPairs[appName] = pairs.map(pair => ({
                ...pair,
                appName
            }));
            
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(allPairs));
        } catch (error) {
            console.error('Error storing key-value pairs:', error);
        }
    }
    
    static addKeyValuePair(appName: string, pair: Omit<KeyValuePair, 'appName'>): void {
        const existingPairs = this.getKeyValuePairs(appName);
        const newPair: KeyValuePair = { ...pair, appName };
        
        const existingIndex = existingPairs.findIndex(p => p.key === pair.key);
        if (existingIndex >= 0) {
            existingPairs[existingIndex] = newPair;
        } else {
            existingPairs.push(newPair);
        }
        
        this.setKeyValuePairs(appName, existingPairs);
    }
    
    static removeKeyValuePair(appName: string, key: string): void {
        const existingPairs = this.getKeyValuePairs(appName);
        const filteredPairs = existingPairs.filter(p => p.key !== key);
        this.setKeyValuePairs(appName, filteredPairs);
    }
    
    static getValue(key: string, appName?: string): string | undefined {
        const pairs = this.getKeyValuePairs(appName);
        const pair = pairs.find(p => p.key === key);
        return pair?.value;
    }
    
    static getAllKeys(appName?: string): string[] {
        const pairs = this.getKeyValuePairs(appName);
        return pairs.map(p => p.key);
    }
    
    static clearAll(appName?: string): void {
        if (appName) {
            this.setKeyValuePairs(appName, []);
        } else {
            localStorage.removeItem(this.STORAGE_KEY);
        }
    }
    
    static exportKeyValuePairs(appName?: string): string {
        const pairs = this.getKeyValuePairs(appName);
        return JSON.stringify(pairs, null, 2);
    }
    
    static importKeyValuePairs(appName: string, jsonData: string): boolean {
        try {
            const pairs: KeyValuePair[] = JSON.parse(jsonData);
            if (!Array.isArray(pairs)) {
                throw new Error('Invalid format: expected array of key-value pairs');
            }
            
            const validPairs = pairs.filter(pair => 
                pair && typeof pair.key === 'string' && typeof pair.value === 'string'
            );
            
            this.setKeyValuePairs(appName, validPairs);
            return true;
        } catch (error) {
            console.error('Error importing key-value pairs:', error);
            return false;
        }
    }
}
